import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { flatten } from 'lodash';
import dataTestValue from '../../utils/testUtils';
import { throttle } from 'lodash';
import { ColumnTitle } from './SimpleDataTable.style';

class SimpleDataTable extends React.Component {
    static propTypes = {
        className: PropTypes.string,
        recordClassName: PropTypes.func,
        header: PropTypes.bool,
        stickyHeader: PropTypes.bool,
        stickyHeaderNew: PropTypes.bool,
        columns: PropTypes.any,
        data: PropTypes.any,
        config: PropTypes.object,
        expanded: PropTypes.func,
        splitRow: PropTypes.func
    };

    static defaultProps = {
        header: false,
        config: {}
    };

    constructor(props, context) {
        super(props, context);
        this.tableRef = React.createRef(null);
        this.theadRef = React.createRef(null);
        this.state = {
            currentWidth: null,
            expandedRecords: []
        };

        this.onTableScroll = this.onTableScroll.bind(this);
    }

    onTableScroll(event) {
        const thead = this.theadRef.current;

        if (thead) {
            thead.style.transform = `translateY(${event.target.scrollTop}px)`;
        }
    }

    componentDidMount() {
        this.listener = () => {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => {
                this.updateWidth();
            }, 180);
        };

        window.addEventListener('resize', this.listener);

        this.updateWidth();
    }

    componentDidUpdate() {
        this.updateWidth();
    }

    componentWillUnmount() {
        clearTimeout(this.timeout);

        if (this.listener && typeof window !== 'undefined') {
            window.removeEventListener('resize', this.listener);
        }
    }

    updateWidth() {
        const currentWidth = this.tableRef?.current?.offsetWidth ?? 0;
        if (currentWidth === this.state.currentWidth) {
            return;
        }

        this.setState({
            currentWidth
        });
    }

    hasEnoughImportance(column) {
        const { currentWidth } = this.state;
        return currentWidth === null || currentWidth >= column.minWidth;
    }

    getRecordClassName(record, index, otherClassName) {
        let classes = [
            'simple-data-table__row--index-' + index,
            otherClassName
        ];

        if (this.state.expandedRecords.indexOf(record.id) !== -1) {
            classes.push('simple-data-table__row--expanded');
        }

        if (this.props.recordClassName) {
            classes.push(this.props.recordClassName(record));
        }

        return classes.join(' ');
    }

    getColumnClassName(column, record) {
        let className = [column.name];

        if (column.className) {
            if (typeof column.className === 'function') {
                let columnClass = column.className(record);

                if (columnClass) {
                    className.push(columnClass);
                }
            } else {
                className.push(column.className);
            }
        }

        return classNames([
            column.name,
            typeof column.className === 'function' ? column.className(record) : column.className
        ]);
    }

    toggle(id) {
        let { expandedRecords } = this.state;
        let idx = expandedRecords.indexOf(id);

        if (idx === -1) {
            expandedRecords.push(id);
        } else {
            expandedRecords.splice(idx, 1);
        }

        this.setState({
            expandedRecords: expandedRecords.slice()
        });
    }

    render() {
        const { columns, data, className, header, stickyHeader, config, expanded, splitRow, stickyHeaderNew } = this.props;
        const { expandedRecords, currentWidth } = this.state;

        const refValue = stickyHeaderNew ? null : this.theadRef;

        if (currentWidth === null) {
            return <div />;
        }

        let cols = columns.filter((column) => this.hasEnoughImportance(column));

        let headerEl;
        if (header && data.length > 0) {
            headerEl = (
                <thead ref={refValue} className={classNames({ 'show-spans': stickyHeader })}>
                    <tr>
                        {
                            cols.map(column => {
                                return (
                                    <ColumnTitle
                                        className={this.getColumnClassName(column)}
                                        key={column.name}
                                        stickyHeaderNew={stickyHeaderNew}
                                    >
                                        {column.label}
                                        <div>{column.label}</div>
                                    </ColumnTitle>
                                );
                            })
                        }
                    </tr>
                </thead>
            );
        }

        let containerClassName = ['simple-data-table-container'];

        if (className) {
            containerClassName.push(className);
        }

        if (headerEl) {
            containerClassName.push('simple-data-table-container--header');
        }

        let containerProps = {};

        if (stickyHeader) {
            containerProps = {
                ...containerProps,
                onScroll: this.onTableScroll
            };
        }

        let isOdd = true;
        return (
            <div className={containerClassName.join(' ')} ref={this.tableRef}>
                <div className='simple-data-table-inner-container' {...containerProps}>
                    <table className='simple-data-table'>
                        {headerEl}
                        <tbody>
                            {
                                flatten(data.map((record, key) => {
                                    let isExpanded = expanded && expandedRecords.indexOf(record.id) !== -1;
                                    let innerData = (splitRow && splitRow(record)) || [record];
                                    let rows = innerData.map((innerRecord, innerIndex) => (
                                        <tr
                                            key={key + '-' + innerIndex}
                                            className={this.getRecordClassName(innerRecord, innerIndex, isOdd ? 'odd' : 'even')}
                                            onClick={
                                                (e) => {
                                                    e.preventDefault();
                                                    this.toggle(record.id);
                                                }
                                            }
                                            data-test={'simple-data-table-row ' + dataTestValue(record.id)}
                                        >
                                            {
                                                cols.map(column => {
                                                    let cellValue;
                                                    if (innerRecord.transactionType === "reward") {
                                                        innerRecord.paymentType = innerRecord.paymentDetails.description;
                                                    }
                                                    const value = column.value ? column.value(innerRecord, innerData) : innerRecord[column.name];

                                                    if (column.hasOwnProperty('route')) {
                                                        cellValue = (
                                                            <a href='#'
                                                                onClick={e => {
                                                                    e.preventDefault();
                                                                    window.open(column.route(record));
                                                                }}
                                                            >
                                                                {value}
                                                            </a>
                                                        );
                                                    } else {
                                                        cellValue = value;
                                                    }

                                                    return (
                                                        <td
                                                            className={this.getColumnClassName(column, innerRecord)}
                                                            style={column.style ? column.style(innerRecord, config) : void 0}
                                                            key={column.name}
                                                            data-test={column.name}
                                                        >
                                                            {cellValue}
                                                        </td>
                                                    );
                                                })
                                            }
                                        </tr>
                                    ));

                                    if (isExpanded) {
                                        rows = rows.concat(
                                            <tr key={key + '-expanded'} className='simple-data-table__expanded-row' data-test='simple-data-table-expanded-row'>
                                                <td colSpan={cols.length}>
                                                    {expanded(record, config)}
                                                </td>
                                            </tr>
                                        );
                                    }

                                    isOdd = !isOdd;
                                    return rows;
                                }))
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
}

export default SimpleDataTable;
